import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Calendar,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  ExternalLink,
  Sparkles,
  Clock,
  FileText,
  Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Help() {
  const [employer, setEmployer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      
      let employerId = user.employer_id;
      
      // If user doesn't have employer_id directly, get it from employee record
      if (!employerId) {
        const employees = await api.entities.Employee.filter({ user_email: user.email });
        if (employees.length > 0 && employees[0].employer_id) {
          employerId = employees[0].employer_id;
        }
      }
      
      // Load employer details
      if (employerId) {
        const employers = await api.entities.Employer.filter({ id: employerId });
        if (employers.length > 0) {
          console.log('Loaded employer:', employers[0]);
          setEmployer(employers[0]);
        }
      }
    } catch (err) {
      console.error('Error loading help data:', err);
    } finally {
      setLoading(false);
    }
  };

  const faqs = [
    {
      category: "Enrollment",
      icon: FileText,
      questions: [
        {
          q: "When can I enroll in benefits?",
          a: "You can enroll during your initial enrollment period (typically 30 days from your hire date) or during the annual open enrollment period. You may also qualify for a special enrollment period if you experience a qualifying life event such as marriage, birth of a child, or loss of other coverage."
        },
        {
          q: "How do I enroll in benefits?",
          a: "Navigate to 'Browse Benefits' to view available plans, then go to 'My Benefits' to select and enroll in your chosen plans. Follow the step-by-step enrollment wizard which will guide you through the process."
        },
        {
          q: "What happens if I miss the enrollment deadline?",
          a: "If you miss the enrollment deadline, you'll typically need to wait until the next open enrollment period unless you qualify for a special enrollment period due to a life event. Contact HR immediately if you've missed the deadline."
        }
      ]
    },
    {
      category: "Coverage & Plans",
      icon: HelpCircle,
      questions: [
        {
          q: "What's the difference between HSA and FSA?",
          a: "An HSA (Health Savings Account) is available with high-deductible health plans, has no 'use-it-or-lose-it' rule, and rolls over year to year. An FSA (Flexible Spending Account) has annual contribution limits and you must use the funds within the plan year or lose them."
        },
        {
          q: "Can I change my benefits mid-year?",
          a: "Generally, you can only change benefits during open enrollment or within 30 days of a qualifying life event (marriage, divorce, birth/adoption of a child, loss of other coverage, etc.). Contact HR to report a qualifying event."
        },
        {
          q: "What benefits am I eligible for?",
          a: "Eligibility depends on your employment status and hours worked. Full-time employees typically qualify for all benefits. Visit 'Browse Benefits' to see plans available to you based on your employment status."
        }
      ]
    },
    {
      category: "Dependents",
      icon: Users,
      questions: [
        {
          q: "How do I add dependents to my coverage?",
          a: "Go to 'Family Members' to add your dependents, then include them when enrolling in benefits. You'll need to provide their information including name, date of birth, SSN, and relationship to you."
        },
        {
          q: "Who qualifies as a dependent?",
          a: "Typically, your spouse/domestic partner and children under age 26 qualify as dependents. Some plans may have different age limits or requirements. Check your specific plan documents for details."
        },
        {
          q: "Do I need to provide documentation for dependents?",
          a: "Yes, you may need to provide marriage certificates, birth certificates, or adoption papers when adding dependents. Your HR department will inform you of required documentation."
        }
      ]
    },
    {
      category: "Claims & Costs",
      icon: Clock,
      questions: [
        {
          q: "How do I submit a claim?",
          a: "Most claims are submitted directly by your healthcare provider. For reimbursement claims, visit the 'Claims' section and follow the submission process. Keep all receipts and documentation."
        },
        {
          q: "When will my benefits start?",
          a: "Benefits typically start on the first day of the month following your enrollment, or on your hire date if enrolled during initial enrollment. Check your enrollment confirmation for your specific coverage start date."
        },
        {
          q: "How much do benefits cost?",
          a: "Costs vary by plan and coverage level. You can see the cost breakdown for each plan in the 'Browse Benefits' section. Premiums are typically deducted from your paycheck pre-tax."
        }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-3">Help & Support Center</h1>
        <p className="text-lg text-slate-600">
          Find answers to common questions or get in touch with our support team
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: FAQ Section */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          
          {faqs.map((category, catIdx) => {
            const Icon = category.icon;
            return (
              <Card key={catIdx}>
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="flex items-center gap-3 text-lg">
                    <Icon className="w-5 h-5 text-emerald-600" />
                    {category.category}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {category.questions.map((faq, faqIdx) => {
                    const isExpanded = expandedFaq === `${catIdx}-${faqIdx}`;
                    return (
                      <div key={faqIdx} className="border-b last:border-b-0">
                        <button
                          onClick={() => setExpandedFaq(isExpanded ? null : `${catIdx}-${faqIdx}`)}
                          className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                        >
                          <span className="font-medium text-slate-900 pr-4 text-sm">{faq.q}</span>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          )}
                        </button>
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 pb-4 text-slate-600 text-sm leading-relaxed">
                                {faq.a}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Right: Company Support */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-emerald-600" />
                Contact Support
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {employer?.phone && (
                <a href={`tel:${employer.phone}`} className="block">
                  <Button variant="outline" className="w-full justify-start h-auto py-4 hover:bg-slate-50">
                    <Phone className="w-5 h-5 mr-3 text-blue-600 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-medium">Call HR</p>
                      <p className="text-xs text-slate-500">{employer.phone}</p>
                    </div>
                  </Button>
                </a>
              )}

              {employer?.hr_contact_email && (
                <a href={`mailto:${employer.hr_contact_email}`} className="block">
                  <Button variant="outline" className="w-full justify-start h-auto py-4 hover:bg-slate-50">
                    <Mail className="w-5 h-5 mr-3 text-emerald-600 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-medium">Email Support</p>
                      <p className="text-xs text-slate-500">{employer.hr_contact_email}</p>
                    </div>
                  </Button>
                </a>
              )}

              {employer?.calendly_link && (
                <a 
                  href={employer.calendly_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full justify-start h-auto py-4 hover:bg-slate-50">
                    <Calendar className="w-5 h-5 mr-3 text-purple-600 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-medium">Schedule Meeting</p>
                      <p className="text-xs text-slate-500">Book time with HR</p>
                    </div>
                  </Button>
                </a>
              )}

              {employer?.help_center_url && (
                <a 
                  href={employer.help_center_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full justify-start h-auto py-4 hover:bg-slate-50">
                    <ExternalLink className="w-5 h-5 mr-3 text-orange-600 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-medium">Help Center</p>
                      <p className="text-xs text-slate-500">Browse resources</p>
                    </div>
                  </Button>
                </a>
              )}
            </CardContent>
          </Card>

          {/* AI Assistant Card */}
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-center mb-2">Benefits Assistant</h3>
              <p className="text-sm text-slate-600 text-center mb-3">
                Get instant answers from our AI assistant 24/7
              </p>
              <p className="text-xs text-slate-500 text-center italic">
                Available on all pages via floating button
              </p>
            </CardContent>
          </Card>

          {/* Support Hours Card (if no contact info) */}
          {!employer?.phone && !employer?.hr_contact_email && !employer?.calendly_link && !employer?.help_center_url && (
            <Card>
              <CardContent className="p-6 text-center">
                <HelpCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-sm text-slate-600">
                  Contact information not yet configured. Please check back later or use the Benefits Assistant.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
